import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Cargar sesión al iniciar
  useEffect(() => {
    const savedToken = localStorage.getItem('zenex_admin_token');
    const savedUser = localStorage.getItem('zenex_admin_user');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Respuesta inesperada del servidor');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Error de autenticación');
      }
      
      setToken(data.access_token);
      setUser(data.user);
      
      localStorage.setItem('zenex_admin_token', data.access_token);
      localStorage.setItem('zenex_admin_user', JSON.stringify(data.user));
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [API_URL]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('zenex_admin_token');
    localStorage.removeItem('zenex_admin_user');
  }, []);

  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.[permission] || false;
  }, [user]);

  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    if (!token) throw new Error('No autenticado');
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 401) {
      logout();
      throw new Error('Sesión expirada');
    }

    // Leer body una sola vez y devolver un Response-like con json() seguro
    const text = await response.text();
    const safeResponse = {
      ok: response.ok,
      status: response.status,
      json: () => {
        try {
          return Promise.resolve(JSON.parse(text));
        } catch {
          return Promise.reject(new Error('Respuesta no es JSON válido'));
        }
      },
      text: () => Promise.resolve(text)
    };

    return safeResponse;
  }, [API_URL, token, logout]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    logout,
    hasPermission,
    fetchWithAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
